import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, ArrowUp } from 'lucide-react';

const Footer = () => {
  const { t, i18n } = useTranslation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getServices = () => {
    return i18n.language === 'fr' ? [
      'Consulting IT',
      'Développement Web',
      'Applications Mobile',
      'Automatisation & IA',
      'E-commerce'
    ] : [
      'IT Consulting',
      'Web Development',
      'Mobile Applications',
      'Automation & AI',
      'E-commerce'
    ];
  };

  return (
    <footer className="relative bg-gradient-to-b from-dark-800/50 to-dark-900 border-t border-white/10">
      <div className="container-custom mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_tech-consulting-1/artifacts/fne2dgjf_ChatGPT%20Image%2026%20aou%CC%82t%202025%20a%CC%80%2014_49_28.png" 
                alt="Winstep FZE Logo" 
                className="h-12 w-12 object-contain"
              />
              <div className="text-2xl font-bold glow-text">
                WINSTEP FZE
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-secondary-400" />
                <span className="text-gray-300">{t('footer.location')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-secondary-400" />
                <span className="text-gray-300">contact@winstep.ae</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-secondary-400" />
                <span className="text-gray-300">+971 XX XXX XXXX</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-gradient">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {getServices().map((service, index) => (
                <li key={index}>
                  <button 
                    onClick={() => scrollToSection('services')}
                    className="text-gray-300 hover:text-secondary-400 transition-colors duration-300"
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-gradient">{t('footer.navigation')}</h3>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-gray-300 hover:text-secondary-400 transition-colors duration-300"
                >
                  {t('nav.services')}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('case-studies')}
                  className="text-gray-300 hover:text-secondary-400 transition-colors duration-300"
                >
                  {t('nav.caseStudies')}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-gray-300 hover:text-secondary-400 transition-colors duration-300"
                >
                  {t('nav.about')}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-300 hover:text-secondary-400 transition-colors duration-300"
                >
                  {t('nav.contact')}
                </button>
              </li>
              <li>
                <a href="#privacy" className="text-gray-300 hover:text-secondary-400 transition-colors duration-300">
                  {t('footer.privacy')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Links & Copyright */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-6 mb-4 md:mb-0">
              <a 
                href="https://linkedin.com/company/winstep-fze" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-r from-secondary-500/20 to-primary-500/20 border border-secondary-500/30 rounded-lg flex items-center justify-center hover:from-secondary-500/30 hover:to-primary-500/30 transition-all duration-300 group"
              >
                <Linkedin className="w-5 h-5 text-secondary-400 group-hover:text-secondary-300" />
              </a>
              <a 
                href="https://twitter.com/winstepfze" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-r from-secondary-500/20 to-primary-500/20 border border-secondary-500/30 rounded-lg flex items-center justify-center hover:from-secondary-500/30 hover:to-primary-500/30 transition-all duration-300 group"
              >
                <Twitter className="w-5 h-5 text-secondary-400 group-hover:text-secondary-300" />
              </a>
              <a 
                href="https://instagram.com/winstepfze" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-r from-secondary-500/20 to-primary-500/20 border border-secondary-500/30 rounded-lg flex items-center justify-center hover:from-secondary-500/30 hover:to-primary-500/30 transition-all duration-300 group"
              >
                <Instagram className="w-5 h-5 text-secondary-400 group-hover:text-secondary-300" />
              </a>
            </div>

            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">
                © 2024 Winstep FZE. {t('footer.rights')}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                {t('footer.crafted')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-secondary-500 to-primary-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:from-secondary-600 hover:to-primary-600 transition-all duration-300 z-40 group"
      >
        <ArrowUp className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
      </button>

      {/* Background decoration */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary-500/50 to-transparent"></div>
    </footer>
  );
};

export default Footer;